import { ChevronDown, Check, Building2, RefreshCw, Sparkles } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../ui/common-ui/dropdown-menu";
import type { CloudType, Tenant, CloudResource } from "../types/cloud";

// Import cloud logos
import awsLogo from "../assets/aws-logo.svg";
import gcpLogo from "../assets/gcp-logo.svg";
import azureLogo from "../assets/azure-logo.svg";

export interface CloudConfig {
  type: CloudType;
  name: string;
  resourceLabel: string;
  logo: string;
}

export const cloudConfigs: CloudConfig[] = [
  { type: "AWS", name: "AWS", resourceLabel: "Account", logo: awsLogo },
  { type: "GCP", name: "GCP", resourceLabel: "Project", logo: gcpLogo },
  { type: "AZURE", name: "Azure", resourceLabel: "Subscription", logo: azureLogo },
];

interface CloudSelectorProps {
  tenantList: Tenant[];
  selectedTenant: Tenant | null;
  selectedCloud: CloudType;
  selectedResource: CloudResource | null;
  cloudResources: Record<CloudType, CloudResource[]>;
  onTenantChange: (tenant: Tenant) => void;
  onCloudChange: (cloud: CloudType) => void;
  onResourceChange: (resource: CloudResource) => void;
  onRefreshSnapshots?: () => void;
  isRefreshing?: boolean;
  isLoading?: boolean;
}

export function CloudSelector({
  tenantList,
  selectedTenant,
  selectedCloud,
  selectedResource,
  cloudResources,
  onTenantChange,
  onCloudChange,
  onResourceChange,
  onRefreshSnapshots,
  isRefreshing = false,
  isLoading = false,
}: CloudSelectorProps) {
  const currentCloudConfig = cloudConfigs.find((c) => c.type === selectedCloud)!;
  const currentResources = cloudResources[selectedCloud] || [];

  return (
    <div className="w-full relative flex items-center">
      {/* Unified Selector Container */}
      <div className="w-full flex items-center justify-between p-2 rounded-2xl bg-gradient-to-r from-card/80 to-card/60 backdrop-blur-xl border border-border/50 shadow-lg shadow-sky-500/5 gap-4 lg:gap-8">

        {/* Tenant Dropdown */}
        <div className="flex-1 flex justify-start">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="group flex items-center justify-between px-4 py-2.5 rounded-xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent hover:from-primary/20 hover:via-primary/10 border border-sky-500/20 hover:border-sky-500/40 transition-all duration-300 hover:shadow-md hover:shadow-sky-500/10 w-full max-w-[340px]">
                <div className="flex items-center gap-3 w-full overflow-hidden">
                  <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-gradient-to-br from-primary to-accent shadow-inner flex-shrink-0">
                    <Building2 className="h-4 w-4 text-primary-foreground" />
                  </div>
                  <div className="flex flex-col items-start min-w-0 flex-1">
                    <span className="text-[11px] font-medium text-muted-foreground/70 uppercase tracking-wider">Tenant</span>
                    <span className="text-[15px] font-semibold text-foreground group-hover:text-primary transition-colors truncate w-full text-left">
                      {selectedTenant?.tenantName || "Select"}
                    </span>
                  </div>
                </div>
                <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-all duration-300 group-hover:translate-y-0.5 flex-shrink-0 ml-2" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="min-w-[280px] p-2 bg-popover/95 backdrop-blur-xl border-border/50 shadow-xl shadow-sky-500/10 rounded-xl"
            >
              <div className="flex items-center gap-2 px-3 py-2 mb-2 rounded-lg bg-gradient-to-r from-primary/10 to-transparent">
                <Sparkles className="h-4 w-4 text-sky-500" />
                <span className="text-[13px] font-semibold text-sky-500">Select Tenant</span>
              </div>
              {tenantList.length === 0 ? (
                <div className="px-3 py-4 text-[15px] text-muted-foreground text-center rounded-lg bg-muted/30">
                  No tenants available
                </div>
              ) : (
                <div className="space-y-1 max-h-[300px] overflow-y-auto custom-scrollbar">
                  {tenantList.map((tenant) => {
                    const isSelected = selectedTenant?.tenantName === tenant.tenantName;
                    return (
                      <DropdownMenuItem
                        key={tenant.tenantName}
                        onClick={() => onTenantChange(tenant)}
                        className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-200 ${isSelected
                          ? "bg-gradient-to-r from-primary/20 to-primary/10 text-sky-500"
                          : "hover:bg-accent/50"
                          }`}
                      >
                        <span className={isSelected ? "font-semibold" : "font-medium"}>
                          {tenant.tenantName}
                        </span>
                        {isSelected && (
                          <div className="flex items-center justify-center w-5 h-5 rounded-full bg-sky-500 flex-shrink-0">
                            <Check className="h-3 w-3 text-primary-foreground" />
                          </div>
                        )}
                      </DropdownMenuItem>
                    );
                  })}
                </div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Divider */}
        <div className="w-px h-10 bg-gradient-to-b from-transparent via-border to-transparent hidden sm:block" />

        {/* Cloud Type Selector */}
        <div className="flex-none flex items-center gap-1 p-1.5 rounded-xl bg-muted/30">
          {cloudConfigs.map((cloud) => {
            const isSelected = selectedCloud === cloud.type;
            const resources = cloudResources[cloud.type] || [];
            const isDisabled = resources.length === 0;

            return (
              <button
                key={cloud.type}
                onClick={() => !isDisabled && onCloudChange(cloud.type)}
                disabled={isDisabled}
                className={`
                  relative flex items-center gap-2 px-4 py-2.5 rounded-lg transition-all duration-300 font-medium
                  ${isDisabled
                    ? "opacity-30 cursor-not-allowed"
                    : isSelected
                      ? "bg-gradient-to-br from-card to-card/80 text-foreground shadow-lg shadow-sky-500/10 ring-1 ring-sky-500/30 scale-105"
                      : "text-muted-foreground hover:text-foreground hover:bg-card/50 hover:scale-105"
                  }
                `}
                title={
                  isDisabled
                    ? `No ${cloud.resourceLabel}s available`
                    : undefined
                }
              >
                {isSelected && (
                  <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-primary/10 to-accent/5" />
                )}
                <img
                  src={cloud.logo}
                  alt={cloud.name}
                  className={`relative h-5 w-auto transition-all duration-300 ${isDisabled
                    ? "opacity-30 grayscale"
                    : isSelected
                      ? "opacity-100 drop-shadow-sm"
                      : "opacity-50 grayscale hover:opacity-70"
                    }`}
                />
                <span className="relative text-[15px] hidden md:inline">{cloud.name}</span>
              </button>
            );
          })}
        </div>

        {/* Divider */}
        <div className="w-px h-10 bg-gradient-to-b from-transparent via-border to-transparent hidden sm:block" />

        {/* Resource Dropdown */}
        <div className="flex-1 flex justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="group flex items-center justify-between px-4 py-2.5 rounded-xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent hover:from-primary/20 hover:via-primary/10 border border-sky-500/20 hover:border-sky-500/40 transition-all duration-300 hover:shadow-md hover:shadow-sky-500/10 w-full max-w-[340px]"
                disabled={currentResources.length === 0}
              >
                <div className="flex items-center gap-3 w-full overflow-hidden">
                  <div className="flex flex-col items-end min-w-0 flex-1">
                    <span className="text-[11px] font-medium text-muted-foreground/70 uppercase tracking-wider">
                      {currentCloudConfig.resourceLabel}
                    </span>
                    <span className="text-[15px] font-semibold text-foreground group-hover:text-primary transition-colors truncate w-full text-right">
                      {selectedResource?.name || "Select"}
                    </span>
                  </div>
                </div>
                <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-all duration-300 group-hover:translate-y-0.5 flex-shrink-0 ml-2" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="min-w-[280px] p-2 bg-popover/95 backdrop-blur-xl border-border/50 shadow-xl shadow-sky-500/10 rounded-xl"
            >
              <div className="flex items-center gap-2 px-3 py-2 mb-2 rounded-lg bg-gradient-to-r from-primary/10 to-transparent">
                <img
                  src={currentCloudConfig.logo}
                  alt={currentCloudConfig.name}
                  className="h-4 w-auto"
                />
                <span className="text-[13px] font-semibold text-sky-500">
                  Select {currentCloudConfig.resourceLabel}
                </span>
              </div>
              {currentResources.length === 0 ? (
                <div className="px-3 py-4 text-[15px] text-muted-foreground text-center rounded-lg bg-muted/30">
                  No {currentCloudConfig.resourceLabel.toLowerCase()}s available
                </div>
              ) : (
                <div className="space-y-1 max-h-[300px] overflow-y-auto custom-scrollbar">
                  {currentResources.map((resource) => {
                    const isResourceSelected = selectedResource?.id === resource.id;
                    return (
                      <DropdownMenuItem
                        key={resource.id}
                        onClick={() => onResourceChange(resource)}
                        className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-200 ${isResourceSelected
                          ? "bg-gradient-to-r from-primary/20 to-primary/10 text-sky-500"
                          : "hover:bg-muted/50"
                          }`}
                      >
                        <span className={`truncate ${isResourceSelected ? "font-semibold text-foreground" : "font-medium text-foreground"}`}>
                          {resource.name}
                        </span>
                        {isResourceSelected && (
                          <div className="flex items-center justify-center w-5 h-5 rounded-full bg-sky-500 flex-shrink-0">
                            <Check className="h-3 w-3 text-primary-foreground" />
                          </div>
                        )}
                      </DropdownMenuItem>
                    );
                  })}
                </div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Action Button Area */}
        {onRefreshSnapshots && (
          <>
            {/* Divider */}
            <div className="w-px h-10 bg-gradient-to-b from-transparent via-border to-transparent hidden sm:block" />
            <div className="flex-none flex items-center pr-2">
              <button
                onClick={onRefreshSnapshots}
                disabled={isRefreshing || !selectedTenant || !selectedResource}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-[13px] border transition-all ${isRefreshing ? 'bg-muted/30 text-muted-foreground border-border/30 cursor-not-allowed' : 'bg-primary/10 text-primary border-primary/20 shadow-sm shadow-primary/5 hover:bg-primary/20 hover:border-primary/30 hover:scale-[1.02] active:scale-[0.98]'}`}
                title="Update Dashboard Snapshot"
              >
                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                <span className="hidden md:inline">{isRefreshing ? 'Updating...' : 'Update Snapshot'}</span>
              </button>
            </div>
          </>
        )}
      </div>

      {/* Loading Indicator */}
      {isLoading && (
        <div className="absolute top-1/2 -right-4 -translate-y-1/2 flex items-center justify-center w-8 h-8 rounded-full bg-sky-500/10 animate-pulse">
          <RefreshCw className="h-4 w-4 animate-spin text-sky-500" />
        </div>
      )}
    </div>
  );
}
